import React from "react";
import { Link } from "react-router-dom";

const services = [
  { num: "01", title: "Strategic Business Review", desc: "A full diagnostic of your agency's people, structure and performance. Where you are, where you should be, and exactly what it will take to close the gap.", to: "/services" },
  { num: "02", title: "Recruitment", desc: "Proactive, headhunted, culture-fit recruitment across every role in real estate. From admin to General Manager — we've placed them all.", to: "/recruitment" },
  { num: "03", title: "Remuneration Benchmarking", desc: "Independent, up-to-date salary and commission data across every real estate role in Victoria. Know what you should be paying — and what your competitors are.", to: "/services" },
  { num: "04", title: "Culture & Growth", desc: "Building the kind of culture people don't leave. Values, rituals, communication, leadership — the invisible architecture that holds a high-performing agency together.", to: "/services" },
  { num: "05", title: "People & Culture Diagnostic", desc: "A structured deep dive into your team — engagement, retention risk, capability gaps and honest feedback the principals never hear.", to: "/services" },
  { num: "06", title: "Workshops & Speaking", desc: "Practical sessions for leadership teams, principals and industry events — delivered by someone who has actually run the business, not read about it.", to: "/speaking" }
];

const Home = () => {
  return (
    <>
      <section className="hero">
        <div className="hero-bg">PEOPLOLOGY</div>
        <span className="eyebrow">Real Estate Business Consultant</span>
        <h1>People &amp; Culture.<br /><em>Recruitment.</em></h1>
        <div className="hero-bottom">
          <p className="hero-sub">Peoplology is the strategic people partner for boutique real estate agencies serious about growth. Bold advice. Deliberate recruitment. Matter-of-fact consulting — from someone who has actually run the business.</p>
          <div className="hero-actions">
            <Link to="/contact" className="btn btn-gold">Book a discovery call</Link>
            <Link to="/services" className="btn btn-outline">See our services</Link>
          </div>
        </div>
      </section>

      <div className="ticker-bar">
        <div className="ticker-track">
          {[...Array(2)].flatMap((_,x)=>["Recruitment","Strategic Reviews","Culture & Growth","Remuneration Benchmarking","People Diagnostic","Workshops","Speaking","Executive Search","Retention Strategy"].map((r,i)=>(
            <span key={`${x}-${i}`} className="ticker-item">{r}</span>
          )))}
        </div>
      </div>

      <section className="section section-black">
        <div className="two-col" style={{marginBottom:'4rem'}}>
          <div>
            <span className="eyebrow">What we do</span>
            <h2 className="h-lg" style={{color:'var(--white)'}}>Everything a great<br />agency needs from<br /><em style={{fontStyle:'normal',color:'var(--gold)'}}>its people partner.</em></h2>
          </div>
          <p className="body-lg" style={{alignSelf:'end'}}>We work across the full people lifecycle — from hiring the right person, to keeping them, growing them, and building the culture that makes it all possible. Six services. One consistent standard.</p>
        </div>

        <div className="three-col">
          {services.map(s => (
            <div key={s.num} className="service-card">
              <span className="service-num">{s.num}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
              <Link to={s.to}>Learn more →</Link>
            </div>
          ))}
        </div>
      </section>

      <div className="stat-strip">
        <div className="stat-item"><span className="stat-num">15+</span><span className="stat-label">Years in real estate</span></div>
        <div className="stat-item"><span className="stat-num">6</span><span className="stat-label">Major agencies</span></div>
        <div className="stat-item"><span className="stat-num">150+</span><span className="stat-label">Placements made</span></div>
        <div className="stat-item"><span className="stat-num">100%</span><span className="stat-label">Real estate focused</span></div>
      </div>

      <section className="section section-black2">
        <div className="two-col" style={{alignItems:'center'}}>
          <div>
            <span className="eyebrow">Meet Laura</span>
            <h2 className="h-lg" style={{color:'var(--white)',marginBottom:'1.5rem'}}>From GM to your<br /><em style={{fontStyle:'normal',color:'var(--gold)'}}>silent partner.</em></h2>
            <p className="body-lg" style={{marginBottom:'1.25rem'}}>Laura Hurle founded Peoplology after 15+ years inside some of Australia's most respected real estate agencies — from administration all the way to General Manager. That experience is not background. It is the product.</p>
            <p className="body-lg" style={{marginBottom:'2rem'}}>When you work with Peoplology, you work with Laura — directly. No junior consultants. No handoffs. Just someone who has walked every hallway of a real estate business.</p>
            <Link to="/about" className="btn btn-gold">Read Laura's story</Link>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:0,margin:'-4px'}}>
            {["Colliers International","Jellis Craig","Kay & Burton","McGrath Estate Agents","Barry Plant","Hocking Stuart"].map(a=>(
              <span key={a} className="agency-badge">{a}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-black">
        <div style={{marginBottom:'4rem'}}>
          <span className="eyebrow">How we work</span>
          <h2 className="h-lg" style={{color:'var(--white)'}}>Three principles.<br />Zero compromise.</h2>
        </div>
        <div className="value-row">
          <div className="value-item">
            <span className="value-word">Bold.</span>
            <p>We say what needs to be said. If your culture is the problem, we'll tell you. Our advice is direct, our standards are high, and we don't soften the truth to make you feel comfortable.</p>
          </div>
          <div className="value-item">
            <span className="value-word">Deliberate.</span>
            <p>Every engagement is intentional. Every placement is personal. We work with a select group of agencies who are serious about their people and serious about growth.</p>
          </div>
          <div className="value-item">
            <span className="value-word">Matter‑of‑fact.</span>
            <p>No jargon, no corporate speak, no fluffy frameworks. We give you what you need — a clear picture of where you are and exactly what it will take.</p>
          </div>
        </div>
      </section>

      <section className="section section-navy2">
        <div style={{maxWidth:'580px',marginBottom:'3.5rem'}}>
          <span className="eyebrow">What people say</span>
          <h2 style={{fontSize:'clamp(2rem,3.5vw,3.5rem)',fontWeight:800,color:'var(--white)',lineHeight:1,letterSpacing:'-0.04em'}}>Real words.<br />Real results.</h2>
        </div>
        <div className="testi-row">
          {[
            ["Peoplology found us a property manager who was exactly the culture fit we needed. Within a month they were our top performer. They just get it.","Sarah B.","Director — boutique agency, Melbourne"],
            ["Laura was the first recruiter who actually listened to what I wanted — not just what I'd done. She found me a role I genuinely love.","Jess M.","Property Manager — placed by Peoplology"],
            ["We'd tried three other recruiters. None of them understood what we actually needed. Peoplology got it in the first call.","Marcus K.","Principal — independent agency, Geelong"]
          ].map(([t,n,r])=>(
            <div className="testi" key={n}>
              <span className="testi-mark">“</span>
              <p className="testi-text">{t}</p>
              <div className="testi-name">{n}</div>
              <div className="testi-role">{r}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-black" style={{textAlign:'center',borderTop:'1px solid var(--border)'}}>
        <span className="eyebrow" style={{textAlign:'center'}}>Ready when you are</span>
        <h2 className="h-xl" style={{color:'var(--white)',marginBottom:'1.25rem'}}>Let's build the agency<br /><em style={{fontStyle:'normal',color:'var(--gold)'}}>you actually want.</em></h2>
        <p style={{fontSize:'1rem',color:'rgba(255,255,255,0.70)',fontWeight:300,maxWidth:'460px',margin:'0 auto 2.5rem',lineHeight:1.8}}>A 20-minute call is all it takes. No pitch, no pressure — just an honest conversation about your agency and what you need next.</p>
        <div style={{display:'flex',gap:'1rem',justifyContent:'center',flexWrap:'wrap'}}>
          <Link to="/contact" className="btn btn-gold">Book a discovery call</Link>
          <Link to="/recruitment" className="btn btn-outline">Explore recruitment</Link>
        </div>
      </section>
    </>
  );
};

export default Home;
